import React, { useEffect, useMemo, useState } from "react";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import FormError from "../../../../Components/v2/Form/FormError";
import Stats from "../../../../Components/v2/Stats";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import TableHeader from "../../../../Components/v2/Table/TableHeader";
import Table from "../../../../Components/v2/Table";
import Pagination from "../../../../Components/v2/Pagination";
import TransactionModal from "../../../../Components/v2/Accounting/TransactionModal";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import { PlusIcon } from "@heroicons/react/outline";

import {
  requestGetAccountingCashflowStats,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { convertToTitleCase, currencyFormat } from "../../../../Utils";
import { wibStatsMonths } from "../../../../Utils/PeriodUtils";

const AccountingCashFlow = () => {
  const { t, i18n } = useTranslation();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  // Transaction modal state
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionType, setTransactionType] = useState(null);

  const accountingDispatch = useAccountingDispatch();

  const { user, loading: userLoading } = useAuthState();
  const { cashFlows, cashFlowsLoading, cashFlowsError } = useAccountingState();

  const tableLoading = cashFlowsLoading || userLoading;

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      onLoadCashFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mainBusiness?.id, accountingDispatch]);

  const onLoadCashFlows = () => {
    if (user?.mainBusiness?.id) {
      requestGetAccountingCashflowStats(accountingDispatch, {
        businessID: user.mainBusiness.id,
        ...wibStatsMonths(),
      });
    }
  };

  const handleClose = (shouldRefresh) => {
    setIsTransactionModalOpen(false);
    setTransactionType(null);
    if (shouldRefresh) {
      onLoadCashFlows();
    }
  };

  // Dropdown items for cash basis transactions only (Income & Expense)
  const dropDownItems = useMemo(
    () => [
      {
        name: t("accounting.transactions.action.income"),
        onClick: () => {
          setTransactionType("INCOME");
          setIsTransactionModalOpen(true);
        },
      },
      {
        name: t("accounting.transactions.action.expense"),
        onClick: () => {
          setTransactionType("EXPENSE");
          setIsTransactionModalOpen(true);
        },
      },
    ],
    [t],
  );

  const statsData = useMemo(() => {
    const calculatePercentageChange = (current, previous) => {
      const currentValue = typeof current === "number" ? current : 0;
      const previousValue = typeof previous === "number" ? previous : 0;

      if (previousValue === 0) {
        return {
          change: currentValue,
          direction: currentValue > 0 ? "up" : "no-change",
          color: currentValue > 0 ? "green" : "gray",
        };
      }

      const change = ((currentValue - previousValue) / previousValue) * 100;

      if (change === 0) {
        return {
          change: 0,
          direction: "no-change",
          color: "gray",
        };
      }

      return {
        change: change.toFixed(2) + "%",
        direction: change >= 0 ? "up" : "down",
        color: change >= 0 ? "green" : "red",
      };
    };

    const currentNet = cashFlows?.stats?.current?.net || 0;
    const previousNet = cashFlows?.stats?.previous?.net || 0;
    const currentInflow = cashFlows?.stats?.current?.inflow || 0;
    const previousInflow = cashFlows?.stats?.previous?.inflow || 0;
    const currentOutflow = cashFlows?.stats?.current?.outflow || 0;
    const previousOutflow = cashFlows?.stats?.previous?.outflow || 0;
    const inflowTransactions =
      cashFlows?.stats?.current?.inflowTransactions || 0;
    const outflowTransactions =
      cashFlows?.stats?.current?.outflowTransactions || 0;

    const netChange = calculatePercentageChange(currentNet, previousNet);
    const inflowChange = calculatePercentageChange(
      currentInflow,
      previousInflow,
    );
    const outflowChange = calculatePercentageChange(
      currentOutflow,
      previousOutflow,
    );

    return [
      {
        title: t("cashFlow.stats.netCashFlow"),
        value: currentNet,
        footer:
          netChange.change === 0
            ? t("cashFlow.stats.noChange")
            : `${
                typeof netChange.change === "number"
                  ? new Intl.NumberFormat(i18n.language, {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(netChange.change)
                  : netChange.change
              } ${
                netChange.direction === "up"
                  ? t("cashFlow.stats.increase")
                  : t("cashFlow.stats.decrease")
              }`,
        trend: netChange,
      },
      {
        title: t("cashFlow.stats.cashInflow"),
        value: currentInflow,
        footer: t("cashFlow.stats.inflowTransactions", {
          inflowTransactions,
        }),
        trend: inflowChange,
      },
      {
        title: t("cashFlow.stats.cashOutflow"),
        value: currentOutflow,
        footer: t("cashFlow.stats.outflowTransactions", {
          outflowTransactions,
        }),
        trend: outflowChange,
        reverseTrendColor: true,
      },
    ];
  }, [cashFlows?.stats, t, i18n.language]);

  // Calculate the current transactions to display based on pagination
  const paginatedTransactions = useMemo(() => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return cashFlows?.transactions?.slice(startIndex, endIndex) || [];
  }, [cashFlows?.transactions, page, perPage]);

  const SkeletonStats = () => {
    return (
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
          >
            <dt className="text-sm font-medium text-gray-500 truncate">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </dd>
            <dd className="mt-2 flex items-center text-sm text-gray-600">
              <div className="h-4 bg-gray-300 rounded w-1/3"></div>
            </dd>
          </div>
        ))}
      </dl>
    );
  };


  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <AccountingPageHeader
          title="Cash Flow"
          dropDownAction={{
            label: t("accounting.transactions.header.title.add"),
            onClick: () => {
              setTransactionType(null);
              setIsTransactionModalOpen(true);
            },
            icon: <PlusIcon className="h-5 w-5" />,
            items: dropDownItems,
          }}
        />

        {/* Stats */}
        <div className="mt-8 px-4 sm:px-6 lg:px-8">
          {cashFlowsLoading ? (
            <SkeletonStats />
          ) : cashFlowsError ? (
            <FormError
                error={cashFlowsError}
                title="common.errors.dataTitle"
                onRetry={onLoadCashFlows}
              />
          ) : (
            <Stats stats={statsData} />
          )}
        </div>

        {/* Cash Flow Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <TableHeader title={t("cashFlow.table.header")} />
          <Table
            isLoading={tableLoading}
            error={cashFlowsError}
            onRetry={onLoadCashFlows}
            columns={[
              {
                header: "cashFlow.fields.date",
                accessor: (item) => (
                  <span className="text-gray-500">
                    {dayjs(item.transaction.createdAt)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")}
                  </span>
                ),
              },
              {
                header: "cashFlow.fields.descriptions.label",
                accessor: (item) => (
                  <div className="flex items-center max-w-xs whitespace-normal break-words">
                    {!item.transaction.description ? (
                      <span className="text-gray-400 italic">
                        {t("cashFlow.fields.descriptions.emptyDescription")}
                      </span>
                    ) : (
                      <ExpandableDescription
                        text={item.transaction.description}
                      />
                    )}
                  </div>
                ),
              },
              {
                header: "cashFlow.fields.category",
                accessor: (item) => (
                  <span className="text-gray-900">
                    {t(convertToTitleCase(item.transaction?.type || ""))}
                  </span>
                ),
              },
              {
                header: "cashFlow.fields.account",
                accessor: (item) => (
                  <span className="text-gray-900">
                    {item.account?.name || "-"}
                  </span>
                ),
              },
              {
                header: "cashFlow.fields.type",
                accessor: (item) => (
                  <span
                    className={
                      item.credit > 0 ? "text-red-600" : "text-green-600"
                    }
                  >
                    {item.credit > 0
                      ? t("cashFlow.types.outflow")
                      : t("cashFlow.types.inflow")}
                  </span>
                ),
              },
              {
                header: "cashFlow.fields.amount",
                accessor: (item) => (
                  <span
                    className={
                      item.credit > 0 ? "text-red-600" : "text-green-600"
                    }
                  >
                    {item.credit > 0
                      ? currencyFormat(item.credit)
                      : currencyFormat(item.debit)}
                  </span>
                ),
                align: "right",
              },
            ]}
            data={paginatedTransactions}
          />
        </div>

        {/* Pagination Component */}
        <Pagination
          currentPage={page}
          perPage={perPage}
          isLoading={tableLoading}
          onPerPageChange={setPerPage}
          onPageChange={setPage}
          items={cashFlows?.transactions}
          isOffline
        />

        {/* Transaction Modal (Cash Basis Only) */}
        {isTransactionModalOpen && (
          <TransactionModal
            open={isTransactionModalOpen}
            onClose={handleClose}
            transaction={null}
            transactionType={transactionType}
            cashBasisOnly
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingCashFlow;
